import * as React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { Customer, sortOrder } from '../../types'
import { FormattedMessage } from 'react-intl'
import { TableHeader, FittedTable } from '../styled'
import {
  getPayersAction,
  toggleCreatePayerModal,
  deletePayerAction,
  viewPayerAction,
  startEditAction,
} from './types'
import styled from 'styled-components'
import { PlusOutlined, SearchOutlined, EditFilled } from '@ant-design/icons'
import { Button } from 'antd'

import ConfirmDeleteModal from '../ConfirmDeleteModal'
import ButtonGroup from '../ButtonGroup'
import ContributorButton from '../ContributorButton'
import { useOnChangeHandler, usePaginatedData } from '../../redux'
import { flow } from 'fp-ts/lib/function'
import { loadable } from '@library/react-toolkit'

const FlexedEnd = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Payers = () => {
  const { data, count, loading } = usePaginatedData(s => s.payers.payers, 'id')
  const { page, itemsPerPage, sort } = useSelector(s => s.payers.query)
  const deleteResult = useSelector(s => s.payers.deleteResult)
  const onChangeHandler = useOnChangeHandler<Customer>(getPayersAction)
  const dispatch = useDispatch()
  const toggle = () => dispatch(toggleCreatePayerModal())
  const deletePayer = flow(deletePayerAction, dispatch)
  const viewPayer = flow(viewPayerAction, dispatch)
  const editPayer = flow(startEditAction, dispatch)

  const [hoveredID, setHoveredID] = React.useState<number | undefined>(
    undefined
  )

  return (
    <FittedTable<Customer & { key: string }>
      size="middle"
      title={() => (
        <TableHeader title={<FormattedMessage id="customers" />}>
          <FlexedEnd>
            <ContributorButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={toggle}
            >
              <FormattedMessage id="newCustomer" />
            </ContributorButton>
          </FlexedEnd>
        </TableHeader>
      )}
      dataSource={data}
      loading={loading}
      pagination={{ pageSize: itemsPerPage, current: page + 1, total: count }}
      columns={[
        {
          sorter: true,
          sortOrder: sortOrder('name', sort),
          title: <FormattedMessage id="name" />,
          dataIndex: 'name',
          key: 'name',
        },
        {
          sorter: true,
          sortOrder: sortOrder('email', sort),
          title: <FormattedMessage id="email" />,
          dataIndex: 'email',
          key: 'email',
        },
        {
          sorter: true,
          sortOrder: sortOrder('address', sort),
          title: <FormattedMessage id="address" />,
          dataIndex: 'address',
          key: 'address',
        },
        {
          sorter: true,
          sortOrder: sortOrder('phoneNumber', sort),
          title: <FormattedMessage id="phonenumber" />,
          dataIndex: 'phoneNumber',
          key: 'phoneNumber',
        },
        {
          sorter: true,
          sortOrder: sortOrder('taxNumber', sort),
          title: <FormattedMessage id="taxNumber" />,
          dataIndex: 'taxNumber',
          key: 'taxNumber',
        },
        {
          title: <FormattedMessage id="actions" />,
          key: 'action',
          fixed: 'right',
          align: 'right',
          render: (_, c) => (
            <ButtonGroup selected={c.id === hoveredID} align="right">
              <Button
                icon={<SearchOutlined />}
                onClick={() => viewPayer(c.id)}
              />
              <ContributorButton
                icon={<EditFilled />}
                onClick={() => editPayer(c)}
              />
              <ConfirmDeleteModal
                item={c}
                deleteItem={deletePayer}
                loading={loadable.isLoading(deleteResult)}
              >
                <FormattedMessage id="deleteCustomerPrompt" values={c} />
              </ConfirmDeleteModal>
            </ButtonGroup>
          ),
        },
      ]}
      onRow={b => ({
        onMouseEnter: () => setHoveredID(b.id),
        onMouseLeave: () =>
          b.id === hoveredID ? setHoveredID(undefined) : undefined,
      })}
      onChange={onChangeHandler}
      scroll={{ y: '' }}
    />
  )
}

export default Payers
